import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, Flex, Button, Text } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stage, Layer, Image as KonvaImage, Text as KonvaText } from "react-konva";
import SolveHeader from "./SolveHeader";
import MultipleChoiceDisplay from "./MultipleChoiceDisplay";
import SolveAnswerBox from "./solve/SolveAnswerBox";
import PaperCanvas from "./solve/PaperCanvas";
import PaperPanel from "./solve/PaperPanel";
import QuestionSidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from "./solve/QuestionSidebar";
import { useQuestionGroups } from "./solve/useQuestionGroups";
import { usePaperAnnotations } from "./solve/usePaperAnnotations";
import { usePaperBoards } from "./solve/usePaperBoards";
import PaperBoardLayer from "./solve/PaperBoardLayer";
import ShareModal from "./solve/ShareModal";
import FigureFrame from "./solve/FigureFrame";
import FigureStudio from "./solve/FigureStudio";
import BoardToolbar from "../StandeClassroom/board/BoardToolbar";
import { useBoard } from "../StandeClassroom/board/useBoard";
import { TOOLS, SHAPES, DEFAULT_COLOUR, REVERTS_AFTER_USE } from "../StandeClassroom/board/boardTools";
import FeedBack from "./FeedBack";
import { useCallback } from "react";
import SubmitIcon from "../Images/Submit-Icon.svg";
import { BASE_API_URL } from './config';
import {
  createCommentThread,
  createComment,
  normalizeQuestionThreads,
  extractErrorMessage,
  CommentPin,
  CommentTooltip,
  CommentComposer,
  CommentThreadPanel,
} from "./Comments";
import { a, PALETTES, useAppColorMode } from "./appColors";
import { solidButton } from "./buttonStyles";
import { useAppToast } from "./appToast";
import "./app-theme.css";




/**
 * `shared` is a paper opened from a share link (see SharedPaperPage): the
 * same page, read-only, with the paper coming from the link rather than the
 * route, and the board editable only by the owner and collaborators.
 */
const SolvePaper = ({ shared = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useAppColorMode();
  const p = isDark ? PALETTES.dark : PALETTES.light; // literal palette for portalled UI
  const toast = useAppToast(isDark);
  const routeState = shared
    ? {
        questions: shared.questions,
        paper_Guid: shared.user_paper_attempt_guid,
        subject_name: shared.subject_name,
        formattedPaperName: shared.paper_name,
        viewerRole: "SHARED",
        paperMode: "REVIEW",
        feedbackReleased: true,
      }
    : location.state || {};
  const { questions = [], paper_Guid, subject_name, formattedPaperName, time_hour, time_min, viewerRole } = routeState;
  // One question is shown at a time and the list moves between them, which is
  // what keeps a paper from being one long scroll.
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // The classroom's board, over the paper: the toolbar's notes, shapes, text
  // and ink need a surface, and this is it.
  const paperBoard = useBoard();
  // And one for the dotted page around it, which does not scroll with the
  // question: a note left beside the paper stays where it was put.
  const pageBoard = useBoard();
  // Which of the two was worked on last, so undo takes back the latest
  // change rather than the latest change on one particular board.
  const lastBoard = useRef("frame");
  const [boardTool, setBoardTool] = useState(TOOLS.SELECT);
  const [boardShape, setBoardShape] = useState(SHAPES.SQUARE);
  const [boardColour, setBoardColour] = useState(DEFAULT_COLOUR);
  /** A placing tool does its one job and hands the pointer back — the same
   *  rule the classroom board follows, so the two behave as one tool. */
  const releaseBoardTool = useCallback(
    () => setBoardTool((prev) => (REVERTS_AFTER_USE.has(prev) ? TOOLS.SELECT : prev)),
    []
  );
  // The figure opened for working on, if any.
  const [studioFigure, setStudioFigure] = useState(null);
  // The width a figure may take. Measured from the column it lands in, so a
  // diagram is scaled to the paper rather than to the screen.
  const figureColumnRef = useRef(null);
  // The paper's scrolling box, which the page around it follows.
  const paperScrollRef = useRef(null);
  const [figureWidth, setFigureWidth] = useState(0);
  useEffect(() => {
    const node = figureColumnRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setFigureWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [selectedNumber]);
  const [answers, setAnswers] = useState({});
  const [feedbackData, setFeedbackData] = useState({});
  const [loadingFeedback, setLoadingFeedback] = useState({});
  const [images, setImages] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  // { [answerPartGuid]: { [figureQuestionGuid]: { dataURL, at } } } - what each
  // answer box has been handed. `at` changes on every attach so re-adding the
  // same figure after a removal still counts as new.
  const [figureSnapshots, setFigureSnapshots] = useState({});
  const [, setTestStarted] = useState(false);
  const [submitIcon, setSubmitIcon] = useState(SubmitIcon);
  const [timeUp, setTimeUp] = useState(false);
  const token = localStorage.getItem("accessToken");
  const [isUploading, setIsUploading] = useState(false);
  const uploadInProgressRef = useRef(false);
  const [paperMode, setPaperMode] = useState(
    routeState.paperMode || "PRACTICE"
  );
  const permissions = {
    canEdit: paperMode !== "REVIEW",
    canSubmitQuestion: paperMode === "PRACTICE",
    canSubmitPaper: paperMode === "CLASSROOM" || paperMode === "PRACTICE",
  };
  // The board over the paper is the student's own working, so it stays with
  // them in every mode — reading feedback is when a note in the margin is
  // most wanted. A teacher reviewing someone else's paper has comments for
  // that instead; the board belongs to whoever sat the paper.
  //
  // Opened from a share link, the owner and collaborators work on the board
  // and everyone else sees it as it is.
  const canAnnotate = shared
    ? shared.access !== "VIEWER"
    : !(paperMode === "REVIEW" && viewerRole === "INSTRUCTOR");
  const showsBoard = canAnnotate || Boolean(shared);
  // The owner shares; nobody reviewing or visiting does.
  const canShare = !shared && Boolean(paper_Guid) && viewerRole !== "INSTRUCTOR";
  const [shareOpen, setShareOpen] = useState(false);
  // One board per question, swapped as the question changes, and the whole
  // set kept between visits. Placed after `permissions`, which it reads.
  const paperBoards = usePaperBoards({
    frame: paperBoard,
    page: pageBoard,
    questionNumber: selectedNumber,
  });
  usePaperAnnotations({
    // A visitor on a public link reads the board from the share itself.
    attemptGuid: canAnnotate ? paper_Guid : null,
    document: paperBoards.document,
    onLoaded: paperBoards.load,
    enabled: canAnnotate,
  });
  const loadBoards = paperBoards.load;
  useEffect(() => {
    if (shared && !canAnnotate) loadBoards(shared.annotations || {});
  }, [shared, canAnnotate, loadBoards]);

  /**
   * Pull the attempt fresh when the paper opens.
   *
   * The questions come from the route state this page was opened with, which
   * a reload restores exactly as it was — so a draft saved minutes earlier
   * was not in it, and an answer only reappeared if the student went back and
   * came in again, letting the previous page fetch it. Asking the server on
   * open makes both routes the same.
   */
  const pulledAttempt = useRef(false);
  useEffect(() => {
    if (shared || pulledAttempt.current || !paper_Guid || !token) return;
    pulledAttempt.current = true;
    (async () => {
      try {
        const response = await fetch(
          `${BASE_API_URL}/api/v1/user-paper-attempts/questions-with-attempt/${paper_Guid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) return;
        const fresh = await response.json();
        if (!Array.isArray(fresh) || fresh.length === 0) return;
        navigate(location.pathname, {
          replace: true,
          state: { ...location.state, questions: fresh },
        });
      } catch (err) {
        // The paper still works from what the route carried.
        console.error("Could not refresh the attempt:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paper_Guid, token]);

  const userGuid = localStorage.getItem("userGuid");
  const currentUserInitial = localStorage.getItem("firstLetter") || "Y";
  const canViewComments = paperMode === "REVIEW" && !shared;
  const canManageComments = paperMode === "REVIEW" && viewerRole === "INSTRUCTOR";
  // Whether the teacher has released feedback for this submission. Instructors
  // review/mark before releasing; students only see marks + comments once it's
  // released. Seeded from the card that opened this page, updated on release.
  const [feedbackReleased, setFeedbackReleased] = useState(
    Boolean(routeState.feedbackReleased)
  );
  const [isReleasing, setIsReleasing] = useState(false);
  const [isRemarking, setIsRemarking] = useState(false);
  // Whether a generated report already exists for this attempt, which decides
  // between offering to generate one and offering to open it.
  const [hasReport, setHasReport] = useState(false);

  // The flag above is a snapshot from the dashboard that opened this page, so it
  // goes stale the moment a teacher releases while the student has the list up.
  // The server is the real gate: it strips marks and feedback from a student
  // until release, so anything that came back means it HAS been released. Trust
  // the payload over the stale flag, otherwise we hide feedback the API sent.
  const serverSentFeedback = questions.some((q) => {
    const attempt = q?.user_question_attempt;
    if (!attempt) return false;
    return (
      (typeof attempt.feedback_text === "string" && attempt.feedback_text.trim() !== "") ||
      attempt.feedback_mark !== null && attempt.feedback_mark !== undefined
    );
  });

  const isStudentAwaitingFeedback =
    paperMode === "REVIEW" &&
    viewerRole === "STUDENT" &&
    !feedbackReleased &&
    !serverSentFeedback;

  // Post-submit confirmation screen (classroom).
  const [submitted, setSubmitted] = useState(false);

  const [threadsByQuestion, setThreadsByQuestion] = useState({});
  const [isPlacingComment, setIsPlacingComment] = useState(false);
  const [pendingComposer, setPendingComposer] = useState(null); // { questionGuid, xPct, yPct, clientX, clientY }
  const [hoveredThread, setHoveredThread] = useState(null); // { id, x, y }
  const [activeThread, setActiveThread] = useState(null); // { questionGuid, threadId, x, y }


  useEffect(() => {
    if (questions.length > 0 && paperMode !== "CLASSROOM") {
      questions.forEach((question) => {
        // Only an attempt that has actually been marked carries feedback.
        // A saved draft has an attempt row and no verdict, and seeding from
        // it put the marker's card on screen with nothing in it — which
        // autosave makes the common case, since every draft creates a row.
        if (question.user_question_attempt?.feedback_correctness) {
          setFeedbackData((prevData) => ({
            ...prevData,
            [question.guid]: {
              mark: question.user_question_attempt.feedback_mark,
              feedback: question.user_question_attempt.feedback_text,
              feedbackStatus: question.user_question_attempt.feedback_correctness,
              sampleImage: question.user_question_attempt.sample_image || "no image url",
              userType: question.user_question_attempt.permission_access
            },
          }));
        }
      });
    }
  }, [questions, paperMode]);

  const buildThreadsByQuestion = useCallback(
    (questionsList) => {
      const next = {};
      questionsList.forEach((question) => {
        next[question.guid] = normalizeQuestionThreads(question, {
          currentUserGuid: userGuid,
          currentUserInitial,
        });
      });
      return next;
    },
    [userGuid, currentUserInitial]
  );

  useEffect(() => {
    if (!canViewComments || questions.length === 0) return;
    setThreadsByQuestion(buildThreadsByQuestion(questions));
  }, [questions, canViewComments, buildThreadsByQuestion]);

  // Comments are only loaded once, from the `questions` this page was opened
  // with - anything the other party (or this user, from another tab) adds
  // afterward won't show up until re-fetched. Rather than requiring a full
  // leave-and-reenter, pull fresh threads whenever the tab regains focus.
  useEffect(() => {
    if (!canViewComments || !paper_Guid) return;

    const refreshCommentThreads = async () => {
      try {
        const response = await fetch(
          `${BASE_API_URL}/api/v1/user-paper-attempts/questions-with-attempt/${paper_Guid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) return;
        const freshQuestions = await response.json();
        if (Array.isArray(freshQuestions)) {
          setThreadsByQuestion(buildThreadsByQuestion(freshQuestions));
        }
      } catch (err) {
        console.error("Could not refresh comment threads:", err);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshCommentThreads();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", refreshCommentThreads);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", refreshCommentThreads);
    };
  }, [canViewComments, paper_Guid, token, buildThreadsByQuestion]);

  const handleToggleCommentMode = () => {
    setIsPlacingComment((prev) => !prev);
  };

  // A student gets a 403 here until feedback is released, which is exactly the
  // point: no button until they are allowed to see it.
  useEffect(() => {
    if (shared || paperMode !== "REVIEW" || !paper_Guid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BASE_API_URL}/api/v1/user-paper-attempts/report/${paper_Guid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setHasReport(res.ok);
      } catch (err) {
        if (!cancelled) setHasReport(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paperMode, paper_Guid, token, feedbackReleased]);

  const handleOpenReport = () => {
    if (!paper_Guid) return;
    navigate(`/classroom/report/${paper_Guid}`, { state: { viewerRole } });
  };

  // Marking runs once, as the student submits. When it comes out wrong - a
  // failed run, a question marked before its scheme was uploaded - the student
  // cannot resubmit to trigger it again, so the teacher asks for it here.
  const handleRemark = async () => {
    if (!paper_Guid || isRemarking) return;
    setIsRemarking(true);
    try {
      const res = await fetch(
        `${BASE_API_URL}/api/v1/user-paper-attempts/remark/${paper_Guid}`,
        { method: "PUT", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("remark failed");
      const result = await res.json();

      // The marks on screen came from the route state this page was opened
      // with, so they are the old ones until we pull them again.
      const refreshed = await fetch(
        `${BASE_API_URL}/api/v1/user-paper-attempts/questions-with-attempt/${paper_Guid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (refreshed.ok) {
        const freshQuestions = await refreshed.json();
        if (Array.isArray(freshQuestions)) {
          navigate(location.pathname, {
            replace: true,
            state: { ...location.state, questions: freshQuestions, feedbackReleased: false },
          });
        }
      }

      // Re-marking un-releases, so the student is back to waiting until this
      // teacher has looked at the new marks and released them again.
      setFeedbackReleased(false);

      const failedNote = result.failed
        ? ` ${result.failed} could not be marked and kept their previous feedback.`
        : "";
      const reportNote = hasReport
        ? " The report was built from the old marks - regenerate it to catch up."
        : "";
      toast({
        title: `Marked ${result.marked} question${result.marked === 1 ? "" : "s"} again`,
        description: `Feedback is unreleased until you release it again.${failedNote}${reportNote}`,
        status: result.failed ? "warning" : "success",
        duration: 6000,
      });
    } catch (err) {
      console.error("Failed to re-mark submission:", err);
      toast({ title: "Could not re-mark this submission", description: "Please try again.", status: "error", duration: 3000 });
    } finally {
      setIsRemarking(false);
    }
  };

  const handleReleaseFeedback = async () => {
    if (!paper_Guid || feedbackReleased || isReleasing) return;
    setIsReleasing(true);
    try {
      const res = await fetch(
        `${BASE_API_URL}/api/v1/user-paper-attempts/release-feedback/${paper_Guid}`,
        { method: "PUT", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("release failed");
      setFeedbackReleased(true);
      toast({
        title: "Feedback released",
        description: "The student can now see their marks, feedback and comments.",
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      console.error("Failed to release feedback:", err);
      toast({ title: "Could not release feedback", description: "Please try again.", status: "error", duration: 3000 });
    } finally {
      setIsReleasing(false);
    }
  };

  useEffect(() => {
    document.body.style.cursor = isPlacingComment ? "crosshair" : "auto";
    return () => {
      document.body.style.cursor = "auto";
    };
  }, [isPlacingComment]);

  useEffect(() => {
    if (!isPlacingComment) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") setIsPlacingComment(false);
    };

    const handlePlacementClick = (e) => {
      const host = e.target.closest("[data-question-guid]");
      if (!host) {
        toast({ title: "Click on a question to leave a comment.", status: "warning", duration: 2500 });
        setIsPlacingComment(false);
        return;
      }

      const rect = host.getBoundingClientRect();
      const xPct = ((e.clientX - rect.left) / rect.width) * 100;
      const yPct = ((e.clientY - rect.top) / rect.height) * 100;

      setPendingComposer({
        questionGuid: host.getAttribute("data-question-guid"),
        xPct: Math.max(0, Math.min(100, xPct)),
        yPct: Math.max(0, Math.min(100, yPct)),
        clientX: Math.min(e.clientX + 10, window.innerWidth - 300),
        clientY: e.clientY,
      });
      setIsPlacingComment(false);
    };

    window.addEventListener("keydown", handleEscape);
    window.addEventListener("click", handlePlacementClick, { capture: true, once: true });
    return () => {
      window.removeEventListener("keydown", handleEscape);
      window.removeEventListener("click", handlePlacementClick, { capture: true });
    };
  }, [isPlacingComment, toast]);

  const handleSubmitFirstComment = async (text) => {
    const { questionGuid, xPct, yPct } = pendingComposer;
    try {
      const thread = await createCommentThread(
        {
          x: Math.round(xPct),
          y: Math.round(yPct),
          user_paper_attempt_guid: paper_Guid,
          question_guid: questionGuid,
          user_guid: userGuid,
        },
        token
      );

      const threadGuid = thread.guid || thread.comment_thread_guid || thread.id;

      const commentRes = await createComment(
        { comment_thread_guid: threadGuid, user_guid: userGuid, comment_text: text },
        token
      );

      const nowISO = commentRes.created_at || commentRes.createdAt || new Date().toISOString();

      const newThread = {
        id: threadGuid,
        questionGuid,
        xPct,
        yPct,
        comments: [
          {
            id: commentRes.id || commentRes.guid || nowISO,
            text,
            userGuid,
            userName: "You",
            userInitial: currentUserInitial,
            createdAt: nowISO,
          },
        ],
      };

      setThreadsByQuestion((prev) => ({
        ...prev,
        [questionGuid]: [...(prev[questionGuid] || []), newThread],
      }));
      setPendingComposer(null);
      toast({ title: "Comment added.", status: "success", duration: 1500 });
    } catch (err) {
      console.error(err);
      toast({
        title: "Could not add comment.",
        description: extractErrorMessage(err),
        status: "error",
        duration: 3000,
      });
      setPendingComposer(null);
    }
  };

  const handleReplyToThread = async (thread, text) => {
    try {
      const commentRes = await createComment(
        { comment_thread_guid: thread.id, user_guid: userGuid, comment_text: text },
        token
      );

      const nowISO = commentRes.created_at || commentRes.createdAt || new Date().toISOString();

      setThreadsByQuestion((prev) => ({
        ...prev,
        [thread.questionGuid]: (prev[thread.questionGuid] || []).map((t) =>
          t.id === thread.id
            ? {
                ...t,
                comments: [
                  ...t.comments,
                  {
                    id: commentRes.id || commentRes.guid || nowISO,
                    text,
                    userGuid,
                    userName: "You",
                    userInitial: currentUserInitial,
                    createdAt: nowISO,
                  },
                ],
              }
            : t
        ),
      }));
    } catch (err) {
      console.error(err);
      toast({
        title: "Could not add reply.",
        description: extractErrorMessage(err),
        status: "error",
        duration: 3000,
      });
    }
  };

  const commentCount = Object.values(threadsByQuestion).reduce(
    (sum, threads) => sum + threads.length,
    0
  );

  const loadImages = (question) => {
    const imgKeys = ['image_1', 'image_2', 'image_3', 'image_4', 'image_5'];
    const loadedImages = {};

    imgKeys.forEach((imgKey) => {
      if (question[imgKey]) {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.src = question[imgKey];
        img.onload = () => {
          setImages((prevImages) => ({
            ...prevImages,
            [`${question.guid}_${imgKey}`]: img,
          }));
        };
        img.onerror = (err) => console.error(`Error loading image: ${question[imgKey]}`, err);
      }
    });
  };

  useEffect(() => {
    if (questions.length > 0) {
      questions.forEach(loadImages);
    }
  }, [questions]);


   
  /**
   * How big to draw a figure so all of it is visible.
   *
   * It is scaled to the column it sits in, not to the window. Sizing against
   * the window was why wide diagrams were cut off: the paper is a 771px panel
   * now, so an image measured against a 1440px screen simply ran past its
   * edge and the panel clipped it. Whichever side is the tighter fit decides
   * the scale, and it is never enlarged past its own size, so nothing is
   * cropped and nothing is blurred.
   */
  /**
   * How big to draw a figure so all of it is visible.
   *
   * It is scaled to the column it sits in, not to the window. Sizing against
   * the window was why wide diagrams were cut off: the paper is a fixed-width
   * panel now, so an image measured against a 1440px screen ran past its edge
   * and the panel clipped it. Whichever side is the tighter fit decides the
   * scale, and it is never enlarged beyond its own size, so nothing is
   * cropped and nothing is blurred.
   */
  const getKonvaImageDimensions = (image) => {
    if (!image || !image.width || !image.height) {
      return { width: 500, height: 300 }; // Fallback values if the image is not loaded
    }
    const maxWidth = figureWidth || window.innerWidth * 0.6;
    const maxHeight = window.innerHeight * 0.75;
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
    return {
      width: Math.round(image.width * scale),
      height: Math.round(image.height * scale),
    };
  };

  const handleUploadStart = () => {
    setIsUploading(true);
  };
  
  const handleUploadEnd = () => {
    setIsUploading(false);
  };
  
  // The paper as questions rather than rows, with how far each has been
  // taken and how each was marked.
  const questionGroups = useQuestionGroups({ questions, answers, feedbackData });

  // Open the first question once the paper is known, and never leave the
  // selection pointing at a question that is no longer there.
  useEffect(() => {
    if (!questionGroups.length) return;
    setSelectedNumber((prev) =>
      prev != null && questionGroups.some((g) => g.number === prev)
        ? prev
        : questionGroups[0].number
    );
  }, [questionGroups]);

  /** The rows of the question on screen. */
  const visibleQuestions = useMemo(
    () => questions.filter((q) => q.number === selectedNumber),
    [questions, selectedNumber]
  );

  /** Clear takes off everything the toolbar put on this page — on the
   *  paper and around it. */
  const clearAnnotationsHere = useCallback(() => {
    paperBoard.clear();
    pageBoard.clear();
  }, [paperBoard, pageBoard]);
  const hasAnnotationsHere = !paperBoard.isEmpty || !pageBoard.isEmpty;

  /** Undo the board last worked on, or the other if that one has nothing
   *  left to take back. */
  const undoAnnotation = useCallback(() => {
    const [first, second] =
      lastBoard.current === "page" ? [pageBoard, paperBoard] : [paperBoard, pageBoard];
    if (first.canUndo()) first.undo();
    else if (second.canUndo()) second.undo();
  }, [paperBoard, pageBoard]);

  // One selection across both boards. Each board listens for Delete and Tab
  // on its own, so a note selected on the page and another on the paper
  // would both be deleted by one key.
  const clearPaperSelection = paperBoard.setSelectedId;
  const clearPageSelection = pageBoard.setSelectedId;
  useEffect(() => {
    if (paperBoard.selectedId) {
      lastBoard.current = "frame";
      clearPageSelection(null);
    }
  }, [paperBoard.selectedId, clearPageSelection]);
  useEffect(() => {
    if (pageBoard.selectedId) {
      lastBoard.current = "page";
      clearPaperSelection(null);
    }
  }, [pageBoard.selectedId, clearPaperSelection]);

  /**
   * The question's words with its figures in place.
   *
   * A figure is a picture to read and a way into the studio; drawing on it
   * happens there or on the board over the paper, so nothing here handles a
   * pointer any more.
   */
  const processQuestionTextWithImages = (text, question, questionGuid) => {
    const elements = [];
    let remainingText = text || "";
  
    const imagePlaceholderRegex = /\[IMAGE_(\d+)\]/g;
    let match;
    let lastIndex = 0;
  
    while ((match = imagePlaceholderRegex.exec(remainingText)) !== null) {
      const placeholderIndex = parseInt(match[1], 10);
      const imageKey = `image_${placeholderIndex}`;
  
      if (match.index > lastIndex) {
        elements.push(remainingText.slice(lastIndex, match.index));
      }
  
      if (question[imageKey] && images[`${question.guid}_${imageKey}`]) {
        const img = images[`${question.guid}_${imageKey}`];
        const dimensions = getKonvaImageDimensions(img);
  
        elements.push(
          <Box key={`${questionGuid}_image_${placeholderIndex}`} width="fit-content" maxW="100%">
          <FigureFrame
            label={`the figure under ${partLabel(question)}`}
            onExpand={
              permissions.canEdit
                ? () =>
                    setStudioFigure({
                      id: `${questionGuid}_${imageKey}`,
                      label: `Figure under ${partLabel(question)}`,
                      image: img,
                      width: img.naturalWidth || img.width,
                      height: img.naturalHeight || img.height,
                      questionNumber: question.number,
                    })
                : undefined
            }
          >
          <Box id={`snapshot-container-${questionGuid}`} position="relative" overflow="hidden" width="fit-content">
            <Stage width={dimensions.width} height={dimensions.height}>
              <Layer>
                <KonvaImage image={img} width={dimensions.width} height={dimensions.height} />
              </Layer>
            </Stage>
          </Box>
          </FigureFrame>
          </Box>
        );
      }
  
      lastIndex = imagePlaceholderRegex.lastIndex;
    }
  
    if (lastIndex < remainingText.length) {
      elements.push(remainingText.slice(lastIndex));
    }
  
    return elements;
  };
  
  /**
   * Put a picture into one part's answer.
   *
   * Both ways of getting a figure across end here — the bar under a figure
   * and the studio — so an answer box never has to know which was used.
   */
  const attachFigureData = useCallback(
    (figureKey, partGuid, drawing) => {
      const replacing = Boolean(figureSnapshots[partGuid]?.[figureKey]);
      setFigureSnapshots((prev) => ({
        ...prev,
        [partGuid]: { ...(prev[partGuid] || {}), [figureKey]: { ...drawing, at: Date.now() } },
      }));
      const target = questions.find((q) => q.guid === partGuid);
      toast({
        title: replacing ? "Drawing updated" : "Drawing added",
        description: `It's now in your answer for ${partLabel(target)}.`,
        status: "success",
        duration: 2600,
        isClosable: true,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [figureSnapshots, questions]
  );

  /** Write a draft without marking it. Stable, so handing it to a box does
   *  not re-wire that box on every render. */
  const autoSaveDraft = useCallback(
    (guid, text, images) => handleAnswerSubmit(guid, text, images, { silent: true }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /** Take a figure back out of a part's answer. */
  const detachFigureData = useCallback((figureKey, partGuid) => {
    setFigureSnapshots((prev) => {
      const forPart = prev[partGuid];
      if (!forPart || !forPart[figureKey]) return prev;
      const next = { ...forPart };
      delete next[figureKey];
      return { ...prev, [partGuid]: next };
    });
  }, []);

  // Captures the figure as the student has drawn on it and hands it to one
  // answer part.
  const handleAnswerSubmit = async (questionGuid, answer, uploadedImages, options = {}) => {
    const isFullPaperSubmit = options.isFullPaperSubmit === true;
    const isSilent = options.silent === true; // no alerts, no spinner
    // Per-question saves (classroom drafts) upsert without feedback; the final
    // submit goes through submit-answer, which runs the AI marker so the teacher
    // has feedback to review. The student just never sees it (gated below +
    // backend release gate).
    const isSaveMode = !isFullPaperSubmit && permissions.canEdit && !permissions.canSubmitQuestion;
    if (!permissions.canSubmitQuestion && !isSaveMode && !isFullPaperSubmit) return;

    if (!answer?.trim() && uploadedImages.length === 0) {
      if (!isSilent) alert("Answer text or attachments cannot be empty. Please provide at least one.");
      return;
    }

    if (!questionGuid) {
      console.warn("Invalid question GUID:", { questionGuid });
      return;
    }

      if (!isSilent) setLoadingFeedback((prev) => ({ ...prev, [questionGuid]: true }));
      const formData = new FormData();
      const startTime = new Date().toISOString();
      const endTime = new Date().toISOString();

      const answerData = {
        completion_status: "COMPLETE",
        start_date_time: startTime,
        updated_date_time: endTime,
        user_answer_text: answer?.trim() || "",
        user_answer_image: "",
        feedback_mark: 0,
        feedback_text: "",
        feedback_correctness: "",
        guid: questionGuid,
        user_paper_attempt_guid: paper_Guid,
        question_guid: questionGuid,
      };

      formData.append("user_question_attempt_str", JSON.stringify(answerData));

      if (uploadedImages.length > 0) {
        // Test mode watches this: a window that loses focus while pictures
        // are going up must not cancel the paper.
        handleUploadStart();
        uploadInProgressRef.current = true;
        try {
          await Promise.all(
            uploadedImages.map(async (image) => {
              const response = await fetch(image.url);
              const blob = await response.blob();
              const file = new File([blob], image.name || "uploaded.png", {
                type: blob.type || "image/png"
              });
              formData.append("images", file);
            })
          );
        } catch (error) {
          console.error("Error uploading images:", error);
          return; // the finally below still clears the flag
        } finally {
          uploadInProgressRef.current = false;
          handleUploadEnd();
        }
      }

      try {
        const endpoint = isSaveMode 
          ? `${BASE_API_URL}/api/v1/user-question-attempts/upsert/`
          : `${BASE_API_URL}/api/v1/user-question-attempts/submit-answer`;
        
        const method = isSaveMode ? "PUT" : "POST";

        const response = await fetch(
          endpoint,
          {
            method: method,
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        const feedback = await response.json();
        // Only PRACTICE papers surface feedback to the solver. Classroom
        // submissions still generate feedback server-side (for the teacher) but
        // must never render it to the student before it's released.
        if (feedback && !isSaveMode && permissions.canSubmitQuestion) {
          setFeedbackData((prevData) => ({
            ...prevData,
            [questionGuid]: {
              mark: feedback.feedback_mark || 0,
              feedback: feedback.feedback_text || "",
              feedbackStatus: feedback.feedback_correctness || "Unknown",
              sampleImage: feedback.sample_image || "no image url",
              guid: feedback.guid,
              userType: feedback.permission_access,
              detail: feedback.detail
            },
          }));
        } else if (!feedback) {
          console.error("Unexpected feedback structure:", feedback);
        }
      } catch (error) {
        console.error("Error submitting answer:", error);
      }
      finally {
        setLoadingFeedback((prev) => ({ ...prev, [questionGuid]: false }));

      }
  };

  const getSubmissionData = (question) => {
    const answerData = answers[question.guid];
    let answerText = "";

    if (typeof answerData === "string") {
      answerText = answerData.trim();
    } else if (answerData && typeof answerData === "object") {
      answerText = (
        answerData.text ||
        answerData.user_answer_text ||
        (typeof answerData.value === "string" ? answerData.value : "")
      ).trim();
    }

    const attachments = [
      ...(answerData?.uploadedImages || []),
      ...(answerData?.annotations || []),
    ];

    return { answerText, attachments };
  };

  const handleSubmitFullPaper = async () => {
    if (!permissions.canSubmitPaper) return;

    const submissionPromises = questions.reduce((promises, question) => {
      const { answerText, attachments } = getSubmissionData(question);
      console.log(answerText)
      console.log(attachments)

      if (answerText || attachments.length > 0) {
        promises.push(handleAnswerSubmit(question.guid, answerText, attachments, { isFullPaperSubmit: true }));
      }

      return promises;
    }, []);

    if (submissionPromises.length === 0) {
      alert("Answer text or attachments cannot be empty. Please provide at least one.");
      return;
    }

    try {
      await Promise.all(submissionPromises);
      
      // After all questions submitted, mark the whole paper as SUBMITTED
      await fetch(`${BASE_API_URL}/api/v1/user-paper-attempts/submit-full-paper-without-feedback/${paper_Guid}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Show the confirmation screen, then head to the dashboard.
      setSubmitted(true);
      setTimeout(() => navigate("/classroom/student/dashboard"), 4000);
    } catch (error) {
      console.error("Error submitting paper:", error);
      toast({
        title: "Submission failed",
        description: "We couldn't submit your paper. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const SubmitStyleButton = {
    padding: "8px 14px",
    alignItems: "center",
    borderRadius: "14px",
    ...solidButton,
    fontSize: "14px",
    fontWeight: "400",
    boxShadow: "0px 0px 5.9px 0px rgba(0, 0, 0, 0.08)",
    width: "194px",
    transition: "background 0.3s, color 0.3s",
    _hover: {
      background: "transparent",
      color: a.text,
    },
    zIndex: "999",
    top: "80%",
    right: "35px",
    position: "fixed",
  };

  const onTimeUp = async () => {
    console.log("Time is up! Submitting all answers.");
    if (timeUp) return;
    setTimeUp(true); // prevent duplicate calls
  
    // Array to hold submission promises
    const submitPromises = [];
  
    // Loop through all questions
    for (const question of questions) {
      const qid = question.guid;
      const answerData = answers[qid];
  
      // Skip if no answer data exists for this question
      if (!answerData) {
        console.warn(`Skipping question ${qid}: No data found.`);
        continue;
      }
  
      // Determine the text answer
      let answerText = "";
      if (typeof answerData === "string") {
        answerText = answerData.trim();
      } else if (typeof answerData === "object" && answerData !== null) {
        answerText = answerData.text ? answerData.text.trim() : "";
      }
  
      // Merge attachments: uploaded images + annotations
      const attachments = [
        ...(answerData.uploadedImages || []),
        ...(answerData.annotations || [])
      ];
  
      // Only submit if there's either non-empty text OR at least one attachment
      if (answerText || attachments.length > 0) {
        console.log(`Submitting question ${qid}:`, { answerText, attachments });
        // Push the promise returned by handleAnswerSubmit into our array
        submitPromises.push(handleAnswerSubmit(qid, answerText, attachments));
      } else {
        console.warn(`Skipping question ${qid}: No text and no attachments.`);
      }
    }
  
    if (submitPromises.length === 0) {
      console.warn("No valid answers were found. Nothing was submitted.");
      return;
    }
  
    try {
      console.log(`Submitting ${submitPromises.length} questions...`);
      await Promise.all(submitPromises);
      console.log("All answers submitted successfully.");
    } catch (error) {
      console.error("Error submitting answers:", error);
    }
  };
  
  
  const partLabel = (part) => {
    if (!part) return "this question";
    let label = `${part.number}`;
    if (part.letter) label += ` ${part.letter})`;
    if (part.letter_part) label += ` ${part.letter_part})`;
    return label;
  };

  // The parts of a question a drawing can go to: the ones that actually take
  // a written answer. Stems with no marks and multiple-choice parts have no
  // box to put it in.
  const generateQuestionParts = (questions, questionNumber) =>
    questions
      .filter((q) => q.number === questionNumber)
      .filter((q) => (q.total_mark || 0) > 0)
      .filter((q) => !(q.answer_format && parseAnswerFormat(q.answer_format).length > 0))
      .map((part) => ({ guid: part.guid, short: partLabel(part), label: `Question ${partLabel(part)}` }));

  let currentQuestionNumber = null;

  const loadExistingAnswers = (question) => {
    const existingAnswerText = question?.user_question_attempt?.user_answer_text || ""; // Text answer
    const selectedOption =
      ["A", "B", "C", "D"].includes(existingAnswerText.trim()) // Check if answer is a valid option
        ? existingAnswerText.trim()
        : ""; // Empty if not a valid option
  
    const existingImages =
      question?.user_question_attempt?.user_question_attempt_attachments?.map(
        (attachment) => attachment.file_url
      ) || [];
  
    return { existingAnswerText, selectedOption, existingImages };
  };
  
  
  const parseAnswerFormat = (answerFormat) => {
    if (answerFormat.includes("[DISPLAY OPTIONS]")) {
      // Return static options A, B, C, D for `[DISPLAY OPTIONS]`
      return [
        { label: "A", text: "" },
        { label: "B", text: "" },
        { label: "C", text: "" },
        { label: "D", text: "" },
      ];
    }
  
    // Default behavior for other formats
    const regex = /\[(\w+)\](.*?)(?=\[\w+\]|$)/gs;
    const options = [];
    let match;
  
    while ((match = regex.exec(answerFormat)) !== null) {
      options.push({ label: match[1], text: match[2].trim() });
    }
  
    return options;
  };
  

  
  
  if (submitted) {
    return (
      <Box
        className="stande-app"
        data-theme={isDark ? "dark" : "light"}
        bg={a.page}
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px="24px"
      >
        <Flex direction="column" align="center" textAlign="center" maxW="440px" gap="6px">
          <Flex
            align="center"
            justify="center"
            boxSize="72px"
            borderRadius="full"
            bg={a.blue}
            mb="18px"
          >
            <Text fontSize="34px" lineHeight="1" color="#000000">✓</Text>
          </Flex>
          <Text fontSize="24px" fontWeight="600" color={a.text}>
            Paper submitted
          </Text>
          <Text fontSize="15px" fontWeight="300" color={a.textSub}>
            Your answers are in. Your teacher will review them and release your feedback — you'll find it on your dashboard once it's ready.
          </Text>
          <Button
            mt="22px"
            px="24px"
            py="10px"
            borderRadius="12px"
            {...solidButton}
            onClick={() => navigate("/classroom/student/dashboard")}
          >
            Go to dashboard
          </Button>
          <Text fontSize="12px" color={a.textMuted} mt="10px">
            Taking you there automatically…
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box className="stande-app" data-theme={isDark ? "dark" : "light"} bg={a.page}>

      {/* Header, list, paper and tools are one surface exactly one window
          tall; the dots run behind all of it. */}
      <PaperCanvas
          isDark={isDark}
          gutter={sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}
          scrollRef={paperScrollRef}
          header={
            <SolveHeader
            subjectName={subject_name}
            paperName={formattedPaperName}
            timeHour={time_hour}
            timeMin={time_min}
            onTimeUp={onTimeUp}
            setTestStarted={setTestStarted}
            isUploading={isUploading}
            uploadInProgressRef={uploadInProgressRef} 
            paperMode={paperMode}
            setPaperMode={setPaperMode}
            onSubmitFullPaper={handleSubmitFullPaper}
            handleAnswerSubmit = {handleAnswerSubmit}
            questions = {questions}
            answers = {answers}
            canManageComments={canManageComments}
            isPlacingComment={isPlacingComment}
            onToggleCommentMode={handleToggleCommentMode}
            commentCount={commentCount}
            onReleaseFeedback={handleReleaseFeedback}
            onOpenReport={handleOpenReport}
            hasReport={hasReport}
            feedbackReleased={feedbackReleased}
            isReleasing={isReleasing}
            onRemark={handleRemark}
            isRemarking={isRemarking}
            onShare={canShare ? () => setShareOpen(true) : undefined} />
          }
          sidebar={
            <QuestionSidebar
              groups={questionGroups}
              selectedNumber={selectedNumber}
              onSelect={setSelectedNumber}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
            />
          }
          backdrop={
            showsBoard ? (
              <PaperBoardLayer
                readOnly={!canAnnotate}
                board={pageBoard}
                tool={boardTool}
                shape={boardShape}
                colour={boardColour}
                onToolUsed={releaseBoardTool}
                onPointerDownCapture={() => {
                  lastBoard.current = "page";
                }}
              />
            ) : null
          }
          toolbar={
            canAnnotate ? (
              <BoardToolbar
                tool={boardTool}
                onToolChange={setBoardTool}
                shape={boardShape}
                onShapeChange={setBoardShape}
                colour={boardColour}
                onColourChange={setBoardColour}
                onClear={clearAnnotationsHere}
                canClear={hasAnnotationsHere}
                onUndo={undoAnnotation}
                isDark={isDark}
              />
            ) : null
          }
        >
          <PaperPanel
            number={selectedNumber}
            contentRef={figureColumnRef}
            scrollRef={paperScrollRef}
            overlay={
              showsBoard ? (
                <PaperBoardLayer
                  readOnly={!canAnnotate}
                  board={paperBoard}
                  tool={boardTool}
                  shape={boardShape}
                  colour={boardColour}
                  onToolUsed={releaseBoardTool}
                  onPointerDownCapture={() => {
                    lastBoard.current = "frame";
                  }}
                />
              ) : null
            }
          >
            {shared && (
              <Flex
                mb="18px"
                px="14px"
                py="10px"
                borderRadius="12px"
                bg={a.greySurface}
                align="center"
                gap="8px"
                fontSize="12.5px"
                color={a.textSub}
              >
                <Text as="span" fontWeight={500} color={a.text}>
                  {shared.access === "OWNER"
                    ? "Your paper"
                    : `Shared by ${shared.owner_name || "a Stande student"}`}
                </Text>
                <Text as="span">
                  {shared.access === "VIEWER"
                    ? "· View only"
                    : shared.access === "COLLABORATOR"
                    ? "· You can draw on this paper with them"
                    : "· Opened from your share link"}
                </Text>
              </Flex>
            )}
            {isStudentAwaitingFeedback && (
              <Box
                mb="20px"
                padding="16px 20px"
                borderRadius="14px"
                border="1px solid"
                borderColor={a.border}
                background={a.warnBg}
              >
                <Text fontSize="15px" fontWeight="500" color={a.text}>
                  Awaiting your teacher's feedback
                </Text>
                <Text fontSize="13px" fontWeight="300" color={a.textSub} mt="2px">
                  Your answers have been submitted. Your marks, feedback and comments will appear here once your teacher has reviewed and released them.
                </Text>
              </Box>
            )}
            {visibleQuestions.map((question, index) => {
              const feedbackForQuestion = feedbackData[question.guid];
              const totalMark = question.total_mark || 0;

              const { existingAnswerText, selectedOption: preselectedOption, existingImages } =
              loadExistingAnswers(question);

              let displayQuestionNumber = false;
              if (currentQuestionNumber !== question.number) {
                currentQuestionNumber = question.number;
                displayQuestionNumber = true;
              } 

              // Parse answer format to check for multiple-choice options
              const options = question.answer_format ? parseAnswerFormat(question.answer_format) : [];

              const isSaveMode = permissions.canEdit && !permissions.canSubmitQuestion;

              const questionThreads = threadsByQuestion[question.guid] || [];

              return (
              <Box
                key={question.guid}
                position="relative"
                mb="26px"
              >
                {canViewComments && (
                    <>
                      {questionThreads.map((t) => (
                        <CommentPin
                          key={t.id}
                          thread={t}
                          hovered={hoveredThread?.id === t.id}
                          onHover={(e) => setHoveredThread({ id: t.id, x: e.clientX, y: e.clientY })}
                          onLeave={() => setHoveredThread(null)}
                          onClick={(e) =>
                            setActiveThread({
                              questionGuid: question.guid,
                              threadId: t.id,
                              x: e.clientX,
                              y: e.clientY,
                            })
                          }
                        />
                      ))}
                      {pendingComposer?.questionGuid === question.guid && (
                        <CommentComposer
                          x={pendingComposer.clientX}
                          y={pendingComposer.clientY}
                          onSubmit={handleSubmitFirstComment}
                          onCancel={() => setPendingComposer(null)}
                        />
                      )}
                    </>
                  )}

                  <Flex direction="column" gap="14px" width="100%">
                    {/* Which part this is. The design shows none, but a
                        student with four boxes in a column needs to know
                        which answer goes where. */}
                    {(question.letter || totalMark > 0) && (
                      <Flex align="center" justify="space-between" gap="10px" width="100%">
                        <Text fontSize="11px" fontWeight="600" color={a.textSub}>
                          {question.letter ? partLabel(question) : ""}
                        </Text>
                        {/* What the part is worth, back on the right where it
                            was before the collapsible card went (2092:742). */}
                        {totalMark > 0 && (
                          <Text
                            flexShrink={0}
                            px="10px"
                            py="3px"
                            borderRadius="200px"
                            bg={a.greySurface}
                            border="1px solid"
                            borderColor={a.edge}
                            color={a.text}
                            fontSize="11px"
                            lineHeight="16px"
                            whiteSpace="nowrap"
                          >
                            {totalMark} {totalMark === 1 ? "mark" : "marks"}
                          </Text>
                        )}
                      </Flex>
                    )}

                    <Text fontSize="13px" lineHeight="19px" width="100%">
                      {processQuestionTextWithImages(
                        question.question_text,
                        question,
                        question.guid
                      )}
                    </Text>

                    {options.length > 0 ? (
                      <MultipleChoiceDisplay
                        questionGuid={question.guid}
                        options={options}
                        existingValue={preselectedOption || ""}
                        onSubmitAnswer={handleAnswerSubmit}
                        onAnswerChange={(value) => {
                          setAnswers((prev) => ({
                            ...prev,
                            [question.guid]: {
                              ...prev[question.guid],
                              text: value,
                              uploadedImages: prev[question.guid]?.uploadedImages || [],
                              annotations: prev[question.guid]?.annotations || [],
                            },
                          }));
                        }}
                        loading={loadingFeedback[question.guid]|| false}
                        disableSubmit={!permissions.canSubmitQuestion && !isSaveMode}
                        isSaveMode={isSaveMode}
                      /> 
                    ) : (
                    totalMark > 0 && (
                      <Box zIndex={10}>
                        <SolveAnswerBox
                          questionGuid={question.guid}
                          questionNumber={question.number}
                          questions={questions}
                          images={images}
                          partLabelOf={partLabel}
                          existingAnswerText={existingAnswerText}
                          existingImages={existingImages}
                          attachedFigures={figureSnapshots[question.guid] || null}
                          onSubmitAnswer={handleAnswerSubmit}
                          // Drafts are saved as they are written, but only
                          // where a draft is a real thing: a practice paper
                          // marks on submit and has nothing to hold.
                          onAutoSave={isSaveMode ? autoSaveDraft : undefined}
                          setAnswers={setAnswers}
                          readOnly={!permissions.canEdit}
                          isDark={isDark}
                          isSubmitting={loadingFeedback[question.guid] || false}
                          // One pen for the page: the colour chosen on the
                          // toolbar is the colour an answer is drawn in.
                          penColour={boardColour}
                          // The verdict belongs with the answer it judges, so
                          // it is handed to the box rather than dropped below
                          // it as a separate card.
                          feedback={
                            feedbackForQuestion && !isStudentAwaitingFeedback ? (
                              <FeedBack
                                loading={loadingFeedback[question.guid] || false}
                                feedbackData={feedbackForQuestion}
                                questionAttemptGuid={question?.user_question_attempt?.guid || feedbackForQuestion.guid || null}
                                totalMarks={totalMark}
                                sampleImage={question?.user_question_attempt?.sample_image || feedbackForQuestion.sampleImage || null}
                                userType={question?.user_question_attempt?.permission_access || feedbackForQuestion.permission_access || null}
                                canEditFeedback={canManageComments}
                                width="100%"
                                marginTop="0"
                              />
                            ) : null
                          }
                        />
                      </Box>
                    )
                  )}

                  </Flex>
              </Box>
              );
            })}
          </PaperPanel>
      </PaperCanvas>

      {canShare && (
        <ShareModal
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          attemptGuid={paper_Guid}
          paperName={[subject_name, formattedPaperName].filter(Boolean).join(" · ")}
          isDark={isDark}
        />
      )}

      <FigureStudio
        isOpen={Boolean(studioFigure)}
        onClose={() => setStudioFigure(null)}
        figure={studioFigure}
        // Only the parts of the question the figure belongs to, and only the
        // ones that actually take an answer.
        parts={studioFigure ? generateQuestionParts(questions, studioFigure.questionNumber) : []}
        onAttach={(partGuid, drawing) =>
          attachFigureData(studioFigure.id, partGuid, drawing)
        }
        onDetach={(partGuid) => detachFigureData(studioFigure.id, partGuid)}
        isDark={isDark}
      />

      {hoveredThread &&
        (() => {
          const thread = Object.values(threadsByQuestion)
            .flat()
            .find((t) => t.id === hoveredThread.id);
          return thread ? <CommentTooltip x={hoveredThread.x} y={hoveredThread.y} thread={thread} /> : null;
        })()}

      <CommentThreadPanel
        isOpen={!!activeThread}
        onClose={() => setActiveThread(null)}
        thread={
          activeThread
            ? (threadsByQuestion[activeThread.questionGuid] || []).find((t) => t.id === activeThread.threadId)
            : null
        }
        anchor={activeThread}
        canReply={canViewComments}
        onReply={handleReplyToThread}
      />
    </Box>
  );
};

export default SolvePaper;



